import { useQueryClient } from "@tanstack/react-query"
import { useRouter } from "@tanstack/react-router"
import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import axios from "axios"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "design-system/atoms/Sheet"
import { Section } from "design-system/molecules/Section"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MailOpen,
  MailPlus,
  RefreshCw,
} from "lucide-react"
import { setAuthToken } from "modules/auth/lib/setAuthToken"
import {
  useEmailReaderCompaniesQuery,
  useEmailReaderMessagesQuery,
  useEmailReaderThreadQuery,
} from "modules/backoffice/hooks/emailReader/useEmailReaderQueries"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { SearchSchema } from "routes/_base/_auth/customers/list/$id"
import {
  BackofficeImpersonateOrganizationResponseDataSchema,
  DataResponse,
  EmailReaderMessagesResponseDataSchema,
  EmailReaderThreadResponseDataSchema,
} from "schema"
import { z } from "zod"

const PAGE_SIZE = 25
const SEARCH_MODES = ["senderEmail", "threadId"] as const
type SearchMode = (typeof SEARCH_MODES)[number]

type MessageRow = z.infer<typeof EmailReaderMessagesResponseDataSchema>["messages"][number]
type ThreadData = z.infer<typeof EmailReaderThreadResponseDataSchema>

const columnHelper = createColumnHelper<Features, MessageRow>()

type EmailReaderProps = {
  // API mount, e.g. "/backoffice/gmailReader" — the shared queries hit `${basePath}/{companies,messages,threads}`.
  basePath: string
  // i18n namespace, e.g. "backoffice.gmailReader" — all reader copy lives under it.
  i18nPrefix: string
  // Optional display transform for tag/label/category chips (Gmail strips underscores).
  formatLabel?: (value: string) => string
}

export default function EmailReader({ basePath, i18nPrefix, formatLabel }: EmailReaderProps) {
  const { t } = useTranslation()
  const rt = useCallback(
    (key: string, options?: Record<string, unknown>) => t(`${i18nPrefix}.${key}`, options),
    [t, i18nPrefix],
  )
  const { buildLocation } = useRouter()
  const queryClient = useQueryClient()
  const { data: companies, isPending: companiesPending } = useEmailReaderCompaniesQuery(basePath)
  const [companyId, setCompanyId] = useState<string | undefined>()
  const [integrationId, setIntegrationId] = useState<string | undefined>()
  const [page, setPage] = useState(1)
  const [pageTokens, setPageTokens] = useState<Record<number, string | undefined>>({ 1: undefined })
  const [selectedMessage, setSelectedMessage] = useState<MessageRow | undefined>()
  const [searchMode, setSearchMode] = useState<SearchMode>("senderEmail")
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")

  const selectedCompany = companies?.find((company) => company.id === companyId)

  useEffect(() => {
    const firstCompany = companies?.at(0)
    if (!firstCompany || companyId) return

    setCompanyId(firstCompany.id)
    setIntegrationId(firstCompany.integrations.at(0)?.id)
  }, [companies, companyId])

  const messagesQuery = useEmailReaderMessagesQuery(
    basePath,
    integrationId
      ? {
          integrationId,
          total: PAGE_SIZE,
          page,
          pageToken: pageTokens[page],
          searchMode,
          search: search || undefined,
        }
      : undefined,
  )

  const threadQuery = useEmailReaderThreadQuery(basePath, {
    integrationId,
    threadId: selectedMessage?.threadId,
  })

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("isRead", {
          header: "",
          cell: ({ getValue }) => {
            const isRead = getValue()
            const Icon = isRead ? MailOpen : MailPlus

            return (
              <Icon
                className="text-muted-foreground size-4"
                aria-label={rt(isRead ? "table.read" : "table.unread")}
              />
            )
          },
          meta: { colSpan: 1, align: "center" as const },
        }),
        columnHelper.accessor("from", {
          header: rt("table.from"),
          cell: ({ getValue }) => (
            <span className="block max-w-72 truncate">{getValue() ?? "-"}</span>
          ),
        }),
        columnHelper.accessor("labels", {
          header: rt("table.labels"),
          cell: ({ getValue }) => {
            const labels = getValue()

            if (!labels.length) return <span className="text-muted-foreground">{"-"}</span>

            return <LabelsBadges labels={labels} formatLabel={formatLabel} className="max-w-56" />
          },
        }),
        columnHelper.accessor("subject", {
          header: rt("table.subject"),
          cell: ({ row, getValue }) => (
            <div className="max-w-xl">
              <div className="truncate font-medium">{getValue() || rt("noSubject")}</div>
              {row.original.snippet && (
                <div className="text-muted-foreground truncate text-xs">{row.original.snippet}</div>
              )}
            </div>
          ),
        }),
        columnHelper.accessor("threadId", {
          header: rt("table.threadId"),
          cell: ({ getValue }) => (
            <span className="text-muted-foreground block max-w-44 truncate font-mono text-xs">
              {getValue()}
            </span>
          ),
        }),
        columnHelper.accessor("date", {
          header: rt("table.date"),
          cell: ({ getValue }) => formatDate(getValue()),
          meta: { align: "right" as const },
        }),
      ]),
    [rt, formatLabel],
  )

  const table = useTable({
    data: messagesQuery.data?.messages ?? [],
    columns,
    features,
  })

  const totalCount = messagesQuery.data?.resultSizeEstimate
  const totalPages = totalCount ? Math.max(Math.ceil(totalCount / PAGE_SIZE), 1) : undefined
  const nextPageToken = messagesQuery.data?.nextPageToken
  const canGoNext = nextPageToken !== undefined && nextPageToken !== null
  const canGoPrevious = page > 1
  const hasSelection = companyId !== undefined && integrationId !== undefined

  function handleCompanyChange(value: string) {
    const company = companies?.find((item) => item.id === value)
    setCompanyId(value)
    setIntegrationId(company?.integrations.at(0)?.id)
    setSelectedMessage(undefined)
    resetPagination()
  }

  function handleIntegrationChange(value: string) {
    setIntegrationId(value)
    setSelectedMessage(undefined)
    resetPagination()
  }

  function handleSearchModeChange(value: SearchMode) {
    setSearchMode(value)
    setSearchInput("")
    setSearch("")
    resetPagination()
  }

  function applySearch() {
    setSearch(searchInput.trim())
    resetPagination()
  }

  function clearSearch() {
    setSearchInput("")
    setSearch("")
    resetPagination()
  }

  function resetPagination() {
    setPage(1)
    setPageTokens({ 1: undefined })
  }

  function goNext() {
    if (!nextPageToken) return
    setPageTokens((current) => ({ ...current, [page + 1]: nextPageToken }))
    setPage((current) => current + 1)
  }

  async function openImpersonatedLink(
    conversation: NonNullable<ThreadData["conversation"]>,
    href: string,
  ) {
    type ImpersonateResponse = DataResponse<
      z.infer<typeof BackofficeImpersonateOrganizationResponseDataSchema>
    >

    const response = await axios.get<ImpersonateResponse>(
      `/backoffice/organizations/impersonate/${conversation.organizationId}`,
    )

    await navigator.clipboard.writeText([window.location.origin, href].join(""))

    setAuthToken({ queryClient, token: response.data.data.token })
    window.open([window.location.origin, href].join(""), "_blank")
  }

  function openConversation(conversation: NonNullable<ThreadData["conversation"]>) {
    const link = buildLocation({
      to: "/conversations/list/$customerId",
      params: { customerId: conversation.customerId },
      search: { tab: "conversations", conversationId: Number(conversation.id) },
    }).href

    openImpersonatedLink(conversation, link)
  }

  function openCustomer(conversation: NonNullable<ThreadData["conversation"]>) {
    const link = buildLocation({
      to: "/customers/list/$id",
      params: { id: conversation.customerId },
      search: SearchSchema.parse({}),
    }).href

    openImpersonatedLink(conversation, link)
  }

  function renderTableContent() {
    if (messagesQuery.isPending || companiesPending) {
      return <TableSkeleton colSpans={columns.map((column) => column.meta?.colSpan ?? 1)} />
    }

    if (!integrationId) {
      return <TableEmptySlate colSpan={columns.length} message={rt("noIntegration")} />
    }

    if (!messagesQuery.data?.messages.length) {
      return <TableEmptySlate colSpan={columns.length} message={rt("empty")} />
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        className="cursor-pointer"
        onClick={() => setSelectedMessage(row.original)}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <div className="flex flex-col gap-6">
      <Section title={rt("title")} description={rt("description")} />

      <div className="flex flex-col gap-3 md:flex-row">
        <Select value={companyId} onValueChange={handleCompanyChange} disabled={companiesPending}>
          <SelectTrigger className="w-full md:w-80">
            <SelectValue placeholder={rt("selectCompany")} />
          </SelectTrigger>
          <SelectContent>
            {companies?.map((company) => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={integrationId}
          onValueChange={handleIntegrationChange}
          disabled={!selectedCompany}
        >
          <SelectTrigger className="w-full md:w-80">
            <SelectValue placeholder={rt("selectAccount")} />
          </SelectTrigger>
          <SelectContent>
            {selectedCompany?.integrations.map((integration) => (
              <SelectItem key={integration.id} value={integration.id}>
                {integration.email}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          onClick={() => messagesQuery.refetch()}
          disabled={!hasSelection || messagesQuery.isFetching}
        >
          <RefreshCw />
          {rt("reload")}
        </Button>
      </div>

      <div className="flex flex-col gap-3 md:flex-row">
        <Select value={searchMode} onValueChange={handleSearchModeChange} disabled={!hasSelection}>
          <SelectTrigger className="w-full md:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SEARCH_MODES.map((mode) => (
              <SelectItem key={mode} value={mode}>
                {rt(`search.modes.${mode}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") applySearch()
          }}
          disabled={!hasSelection}
          placeholder={rt(`search.placeholders.${searchMode}`)}
          className="w-full md:max-w-md"
        />

        <Button
          variant="outline"
          onClick={applySearch}
          disabled={!hasSelection || messagesQuery.isFetching}
        >
          {rt("search.submit")}
        </Button>

        <Button
          variant="ghost"
          onClick={clearSearch}
          disabled={!hasSelection || (!search && !searchInput)}
        >
          {rt("search.clear")}
        </Button>
      </div>

      {hasSelection && (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} {...header.column.columnDef.meta}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>{renderTableContent()}</TableBody>
          </Table>

          <div className="flex items-center justify-between gap-4 px-2">
            <div className="text-muted-foreground text-sm">
              {totalCount === undefined ? null : rt("total", { count: totalCount })}
            </div>
            <div className="flex items-center gap-3">
              {totalPages && (
                <span className="text-muted-foreground text-sm">
                  {t("shared.pagination.page", { current: page, total: totalPages })}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((current) => current - 1)}
                disabled={!canGoPrevious || messagesQuery.isFetching}
              >
                <ChevronLeft />
                {t("shared.pagination.previous")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={goNext}
                disabled={!canGoNext || messagesQuery.isFetching}
              >
                {t("shared.pagination.next")}
                <ChevronRight />
              </Button>
            </div>
          </div>
        </>
      )}

      <Sheet
        open={selectedMessage !== undefined}
        onOpenChange={(open) => {
          if (!open) setSelectedMessage(undefined)
        }}
      >
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-4xl">
          <SheetHeader className="border-b bg-muted/40 p-5 text-left">
            <SheetTitle>{selectedMessage?.subject || rt("noSubject")}</SheetTitle>
            <SheetDescription>{selectedMessage?.threadId}</SheetDescription>
            {selectedMessage?.labels.length ? (
              <LabelsBadges
                labels={selectedMessage.labels}
                formatLabel={formatLabel}
                className="pt-1"
              />
            ) : null}
            {threadQuery.data?.conversation && (
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    threadQuery.data.conversation && openConversation(threadQuery.data.conversation)
                  }
                >
                  <ExternalLink />
                  {rt("openConversation")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    threadQuery.data.conversation && openCustomer(threadQuery.data.conversation)
                  }
                >
                  <ExternalLink />
                  {rt("openCustomer")}
                </Button>
              </div>
            )}
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-5">
            {threadQuery.isPending ? (
              <div className="text-muted-foreground text-sm">{t("shared.loading")}</div>
            ) : (
              <div className="flex flex-col gap-4">
                {threadQuery.data?.messages.map((message) => (
                  <div key={message.id} className="rounded-md border bg-background p-4">
                    <div className="mb-3 grid gap-1 text-sm">
                      <div>
                        <span className="text-muted-foreground">{rt("table.from")}</span>{" "}
                        {message.from ?? "-"}
                      </div>
                      <div>
                        <span className="text-muted-foreground">{rt("table.to")}</span>{" "}
                        {message.to ?? "-"}
                      </div>
                      <div className="text-muted-foreground">{formatDate(message.date)}</div>
                      {message.labels.length ? (
                        <div className="flex items-start gap-2">
                          <span className="text-muted-foreground">{rt("table.labels")}</span>
                          <LabelsBadges labels={message.labels} formatLabel={formatLabel} />
                        </div>
                      ) : null}
                    </div>
                    <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-6">
                      {message.body || message.snippet || rt("emptyBody")}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function formatDate(value: string | null) {
  if (!value) return "-"

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString()
}

function LabelsBadges({
  labels,
  formatLabel,
  className,
}: {
  labels: string[]
  formatLabel?: (value: string) => string
  className?: string
}) {
  return (
    <div className={`flex flex-wrap gap-1 ${className ?? ""}`}>
      {labels.map((label, index) => (
        <Badge key={`${label}-${index}`} variant="secondary" className="max-w-full truncate">
          {formatLabel ? formatLabel(label) : label}
        </Badge>
      ))}
    </div>
  )
}
